/*
 * Discount window
 * ---------------
 * A code that arrives from a campaign link (/discount/CODE) belongs to that click, not to
 * the browser. Shopify keeps the code on the CART, and the cart cookie on this shop lasts
 * three months, so one click keeps a code winning for weeks: it outlives its campaign and
 * blocks the next one, because most of these codes combine with nothing and Shopify
 * silently keeps whichever single discount saves the shopper more. A real case: a shopper
 * clicked the free-necklace link, did not buy, and days later bought three rings on a
 * buy-2-get-1 campaign -- and got the necklace instead of the rings deal.
 *
 * THE RULES
 *
 *   1. A WINDOW. A code is valid for WINDOW_MS from the click, and only in the browser
 *      session the click happened in. Outside it the code comes off the cart, with the
 *      gift unit it brought. This is what saves the rings case: an automatic discount has
 *      nothing to click, so all it needs is for the stale code to be gone.
 *
 *   2. ON A CONFLICT, THE NEWEST CLICK WINS -- and only on a conflict. We ask Shopify
 *      rather than guess: a clicked code that applies means no conflict and nothing is
 *      touched, so codes Shopify is happy to stack keep stacking. A clicked code that is
 *      blocked means the other clicked codes come off and we look again; if it applies
 *      now, that was the blocker, and if it still does not (no matching products, minimum
 *      not met, an automatic discount refusing to combine) everything goes straight back.
 *
 *   3. A GIFT IS FREE OR IT IS GONE. Gift code on the cart, shopper over the threshold,
 *      and no gift line actually being discounted: the gift lost a conflict, so a unit
 *      comes out. Nobody pays $39 for what an email called a gift. Judged by the gift
 *      code's OWN allocation, never by line price -- two necklaces, one free and one she
 *      chose to buy, arrive as two lines, and the paid one is a purchase.
 *
 * WHEN THE RULES RUN: every page load, and every cart change the shopper makes. The
 * second half is not optional -- she can click the 50% link while the cart holds nothing
 * it applies to (rule 2 correctly leaves everything alone) and then add the $19.90 piece
 * from a drawer, which loads no page at all. The answer that was right at click time is
 * wrong one add-to-cart later.
 *
 * ARBITRATION IS REMEMBERED, PER CLICK. The trial costs two cart writes, so its answer is
 * cached against a fingerprint of the cart, the codes on it, and a serial that counts
 * clicks. Without the cart in the fingerprint the add-to-cart case above is missed;
 * without the serial, clicking gift -> 10% -> gift -> 10% lands back on a cart state we
 * have already ruled on, and the fourth click is skipped as a duplicate when it is in
 * fact a fresh instruction.
 *
 * GIFT UNITS ARE COUNTED, NOT FLAGGED. Taking a code off and leaving its gift behind at
 * full price is the worst outcome there is, so every eviction adds one unit to a debt and
 * every verified removal pays one off -- verified against the cart, because a line key can
 * go stale behind us and a write can be cut off by the shopper navigating. A pass that
 * dies mid-flight therefore leaves the debt standing, and the next pass settles it. A
 * fresh click on the gift link pays off exactly ONE unit -- the one she just re-claimed --
 * and leaves any older debt owing, which is what keeps the count honest when clicks and
 * interruptions interleave.
 *
 * THE PAGE IS REPAINTED afterwards: we change the cart after the page rendered, so without
 * that the shop keeps showing the line we removed, at full price, with the bag counting it.
 *
 * WHICH CODES: every code that arrived from a link click, plus MANAGED below, minus
 * EXEMPT. A code she typed at checkout, or one an app applied, is never touched.
 *
 * HOW A CLICK IS RECOGNISED -- and why the email links need no change: the /discount/CODE
 * hop sets Shopify's own `discount_code` cookie, which has no expiry, so it dies with the
 * browser session. The cookie naming a code means exactly "this code was clicked, in this
 * session" -- both halves of rule 1 at once.
 *
 * STATE: open windows, the last arbitration, the click serial and the gift debt live in
 * session cookies (shared across tabs, gone when the browser closes); which codes came
 * from a click lives in localStorage keyed by cart token, because that has to outlive the
 * browser closing; a trace of the last pass lives in sessionStorage for the debug readout.
 *
 * IT CANNOT RUN AT CHECKOUT, so a shopper already paying never loses a discount
 * mid-purchase -- and one who reaches checkout without passing through the shop still
 * carries whatever her cart carried. That gap closes in the emails, not here.
 */
(function () {
  'use strict';

  // Twelve hours from the click: covers an evening's shopping and a come-back-after-dinner,
  // short enough that yesterday's campaign is not still winning.
  var WINDOW_MS = 12 * 60 * 60 * 1000;
  var GRANTS_COOKIE = 'wv_discount_window';
  var ARBITRATED_COOKIE = 'wv_discount_arbitrated';
  var DEBT_COOKIE = 'wv_discount_gift_debt';
  var LAST_CLICK_COOKIE = 'wv_discount_last_click';
  var CLICK_SERIAL_COOKIE = 'wv_discount_click_serial';
  var ORIGINS_KEY = 'wv:discount-window:origins';
  var TRACE_KEY = 'wv:dw:trace';
  var SHOPIFY_COOKIE = 'discount_code';

  // free-gift.js ignores cart writes carrying this header, and so do we: it marks a write
  // as ours, so the two files cannot set each other off in circles.
  var INTERNAL_HEADER = 'X-WV-Free-Gift';

  // Codes that keep their window open no matter what: one printed in a physical insert,
  // one an influencer posts, a permanent service code. Uppercase. Empty means every
  // clicked code is managed.
  var EXEMPT = [];

  /*
   * Codes known up front, keyed uppercase. Two jobs: they are managed even when the click
   * that applied them predates this file (or happened in a private window where
   * localStorage throws), and giftVariantIds ties a code to the gift it brings so the gift
   * unit leaves with it. The ids mirror snippets/free-gift.liquid -- every birth-month
   * variant, verified against the live product 2026-09-21 -- and the live list wins when it
   * is on the page, so the two cannot drift apart at runtime.
   */
  var MANAGED = {
    TREASURE10: {},
    '19.9_COLLECTION': {},
    BIRTHSTONEGIFT: {
      giftVariantIds: [
        51889999053121, 51889999020353, 51889999151425, 51889998922049,
        51889999184193, 51889999118657, 51889999085889, 51889998954817,
        51889999282497, 51889999249729, 51889999216961, 51889998987585
      ]
    }
  };

  var key = function (code) { return String(code || '').toUpperCase(); };
  var same = function (a, b) { return key(a) === key(b); };
  var isExempt = function (code) { return EXEMPT.indexOf(key(code)) !== -1; };
  var isKnown = function (code) {
    return Object.prototype.hasOwnProperty.call(MANAGED, key(code));
  };

  /* --------------------------------------------------------------------- trace */

  var steps = [];

  function note(step) {
    steps.push(step);
    try { sessionStorage.setItem(TRACE_KEY, JSON.stringify(steps)); } catch (e) {}
  }

  /* ------------------------------------------------------------------- cookies */

  function readCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var eq = parts[i].indexOf('=');
      if (eq > -1 && parts[i].slice(0, eq) === name) {
        try { return decodeURIComponent(parts[i].slice(eq + 1)); } catch (e) { return parts[i].slice(eq + 1); }
      }
    }
    return null;
  }

  // No expiry on purpose: these have to end when the browser does.
  function writeSessionCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
  }

  function deleteCookie(name) {
    document.cookie = name + '=; path=/; Max-Age=0; SameSite=Lax';
  }

  function readGrants() {
    var raw = readCookie(GRANTS_COOKIE);
    if (!raw) return {};
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  function writeGrants(grants) {
    writeSessionCookie(GRANTS_COOKIE, JSON.stringify(grants));
  }

  /* ------------------------------------------------------------- the gift debt */

  function readDebt() {
    var raw = readCookie(DEBT_COOKIE);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.code || !(Number(parsed.owed) > 0)) return null;
      return { code: key(parsed.code), owed: Number(parsed.owed) };
    } catch (e) {
      return null;
    }
  }

  function writeDebt(code, owed) {
    if (!(owed > 0)) {
      deleteCookie(DEBT_COOKIE);
      return;
    }
    writeSessionCookie(DEBT_COOKIE, JSON.stringify({ code: key(code), owed: owed }));
  }

  function oweOneUnit(code) {
    var debt = readDebt();
    if (debt && same(debt.code, code)) writeDebt(code, debt.owed + 1);
    else writeDebt(code, 1);
  }

  function payOneUnit(code) {
    var debt = readDebt();
    if (!debt) return;
    if (code && !same(debt.code, code)) return;
    writeDebt(debt.code, debt.owed - 1);
  }

  /* ------------------------------------------------- which codes came from a click */

  function readOrigins(token) {
    try {
      var parsed = JSON.parse(localStorage.getItem(ORIGINS_KEY) || 'null');
      if (!parsed || parsed.token !== token || !parsed.codes) return {};
      return parsed.codes;
    } catch (e) {
      return {};
    }
  }

  function writeOrigins(token, codes) {
    try {
      localStorage.setItem(ORIGINS_KEY, JSON.stringify({ token: token, codes: codes }));
    } catch (e) {
      // Private window, or storage full. The MANAGED list still covers the known codes.
    }
  }

  // Held without a cart token too, because a click can land before the cart exists:
  // /discount/CODE on an empty cart mints a token only once something is added.
  var pendingOrigins = [];

  function cameFromClick(code, origins) {
    if (pendingOrigins.indexOf(key(code)) !== -1) return true;
    return Object.prototype.hasOwnProperty.call(origins, key(code));
  }

  /* -------------------------------------------------------------------- window */

  // Synchronous on purpose: free-gift.js asks this before it decides whether to put the
  // gift line back, and it cannot wait for a cart round-trip.
  function isValid(code) {
    if (isExempt(code)) return true;
    var at = readGrants()[key(code)];
    return typeof at === 'number' && Date.now() - at < WINDOW_MS;
  }

  // The code Shopify's cookie names: the last code clicked in this session. Read fresh
  // each pass, because a pass can follow a click that happened after the page loaded.
  function clickedCode() {
    var clicked = readCookie(SHOPIFY_COOKIE);
    return clicked && !isExempt(clicked) ? clicked : null;
  }

  // Counts clicks. Every time Shopify's cookie names something different from what we saw
  // last, that is a new instruction from the shopper, and any arbitration we cached for an
  // identical-looking cart no longer speaks for it.
  function clickSerial() {
    var current = clickedCode() || '';
    var last = readCookie(LAST_CLICK_COOKIE);
    var serial = Number(readCookie(CLICK_SERIAL_COOKIE)) || 0;

    if (last !== current) {
      serial += 1;
      writeSessionCookie(LAST_CLICK_COOKIE, current);
      writeSessionCookie(CLICK_SERIAL_COOKIE, String(serial));

      // A fresh click on a gift link re-claims exactly one unit, so one unit of debt on
      // that code is settled -- and any older debt stays owing.
      if (current) payOneUnit(current);
    }
    return serial;
  }

  // Opens the window on the first pass that sees the click. The timestamp is left alone
  // afterwards: the cookie lives for the whole session, so refreshing it on every page view
  // would mean the window never closes.
  function openWindowFor(code) {
    if (!code) return;
    if (pendingOrigins.indexOf(key(code)) === -1) pendingOrigins.push(key(code));

    var grants = readGrants();
    if (typeof grants[key(code)] === 'number') return;

    grants[key(code)] = Date.now();
    writeGrants(grants);
  }

  openWindowFor(clickedCode());

  window.WVDiscountWindow = {
    isValid: isValid,
    windowMs: WINDOW_MS,
    managed: MANAGED,
    exempt: EXEMPT,
    grants: readGrants,
    debt: readDebt,
    clicked: clickedCode(),
    steps: function () { return steps.slice(); }
  };

  /* ---------------------------------------------------------------- debug readout */

  // Fetched only when asked for, so a shopper never downloads a tool that is not for her.
  // The address is written into the page by snippets/meta-tags.liquid, because Liquid is the
  // only thing that knows the CDN path -- and the decision has to be made here, because
  // Liquid cannot read a query string.
  (function () {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
    if (params.get('wv_debug') !== '1' || !window.WVDiscountDebugSrc) return;

    var script = document.createElement('script');
    script.src = window.WVDiscountDebugSrc;
    script.defer = true;
    (document.head || document.documentElement).appendChild(script);
  })();

  /* ------------------------------------------------------------------ cart i/o */

  function getCart() {
    var headers = {};
    headers[INTERNAL_HEADER] = '1';
    return fetch('/cart.js', { credentials: 'same-origin', headers: headers }).then(function (r) {
      return r.json();
    });
  }

  function post(url, body) {
    var headers = { 'Content-Type': 'application/json' };
    headers[INTERNAL_HEADER] = '1';
    return fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: headers,
      body: JSON.stringify(body)
    });
  }

  function cartCodes(cart) {
    return (cart.discount_codes || []).map(function (entry) { return entry.code; });
  }

  function setCodes(codes) {
    return post('/cart/update.js', { discount: codes.join(',') }).then(function (response) {
      if (!response || !response.ok) throw new Error('discount update failed');
      return true;
    });
  }

  function giftVariantIds(code) {
    var live = window.WVFreeGift && window.WVFreeGift.giftVariantIds;
    var ids = (live && live.length ? live : (MANAGED[key(code)] || {}).giftVariantIds) || [];
    return ids.map(Number);
  }

  function giftLinesIn(cart, variantIds) {
    return cart.items.filter(function (item) {
      return variantIds.indexOf(item.variant_id) !== -1;
    });
  }

  function giftUnitsIn(cart, variantIds) {
    return giftLinesIn(cart, variantIds).reduce(function (total, item) {
      return total + item.quantity;
    }, 0);
  }

  // Takes ONE gift unit out. Verified against the cart afterwards rather than trusted.
  function removeOneGiftUnit(variantIds) {
    if (!variantIds.length) return Promise.resolve(false);

    return getCart().then(function (cart) {
      var lines = giftLinesIn(cart, variantIds);
      if (!lines.length) return true; // nothing there to remove

      var line = lines[0];
      var target = giftUnitsIn(cart, variantIds) - 1;

      return post('/cart/change.js', { id: line.key, quantity: Math.max(0, line.quantity - 1) })
        .then(getCart)
        .then(function (updated) {
          if (giftUnitsIn(updated, variantIds) <= target) {
            note('removed one gift unit: ' + line.title);
            return true;
          }
          note('gift unit removal did NOT take -- will retry');
          return false;
        });
    });
  }

  /* --------------------------------------------------------- repainting the page */

  function cartSections() {
    var found = [];
    var drawer = document.querySelector('cart-drawer');
    var wrapper = drawer && drawer.closest('.shopify-section');
    var drawerId = wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : null;

    if (drawerId && document.querySelector('#CartDrawer')) {
      found.push({ id: drawerId, target: '#CartDrawer', selector: '#CartDrawer' });
    }
    if (document.getElementById('cart-icon-bubble')) {
      found.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', selector: '.shopify-section' });
    }
    if (document.querySelector('cart-items .js-contents')) {
      found.push({ id: 'main-cart-items', target: 'cart-items .js-contents', selector: '.js-contents' });
    }
    if (document.querySelector('.cart__footer .js-contents')) {
      found.push({ id: 'main-cart-footer', target: '.cart__footer .js-contents', selector: '.js-contents' });
    }
    return found;
  }

  // Same sections and selectors free-gift.js re-renders, so the two cannot disagree about
  // what the cart markup is.
  function repaintCart() {
    var wanted = cartSections();
    if (!wanted.length) return Promise.resolve(false);

    var url = window.location.pathname + '?sections=' + wanted.map(function (s) { return s.id; }).join(',');
    var headers = {};
    headers[INTERNAL_HEADER] = '1';

    return fetch(url, { credentials: 'same-origin', headers: headers })
      .then(function (response) { return response.json(); })
      .then(function (rendered) {
        if (!rendered) return false;
        var painted = false;

        wanted.forEach(function (section) {
          var html = rendered[section.id];
          var element = document.querySelector(section.target);
          if (!html || !element) return;

          var source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
          if (source) {
            element.innerHTML = source.innerHTML;
            painted = true;
          }
        });

        if (painted) note('repainted the cart shown on the page');
        return painted;
      })
      .catch(function () {
        // A stale count is cosmetic and the next navigation fixes it. Never surface this.
        return false;
      });
  }

  /* ----------------------------------------------------------------- bookkeeping */

  function managedCodes(cart, origins) {
    return cartCodes(cart).filter(function (code) {
      if (isExempt(code)) return false;
      return isKnown(code) || cameFromClick(code, origins);
    });
  }

  function forget(removed, token, origins) {
    var grants = readGrants();
    removed.forEach(function (code) { delete grants[key(code)]; });
    writeGrants(grants);

    removed.forEach(function (code) {
      delete origins[key(code)];
      var pending = pendingOrigins.indexOf(key(code));
      if (pending !== -1) pendingOrigins.splice(pending, 1);
    });
    writeOrigins(token, origins);

    var clicked = readCookie(SHOPIFY_COOKIE);
    var namesRemoved = clicked && removed.some(function (code) { return same(code, clicked); });
    if (namesRemoved) deleteCookie(SHOPIFY_COOKIE);
  }

  // Every eviction owes a unit; every verified removal pays one off.
  async function collectGiftUnits(codes) {
    var owing = codes.filter(function (code) { return giftVariantIds(code).length > 0; });
    if (!owing.length) return false;

    var code = owing[0];
    oweOneUnit(code);

    if (await removeOneGiftUnit(giftVariantIds(code))) {
      payOneUnit(code);
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------------- the unpaid debt */

  async function payGiftDebt(cart) {
    var debt = readDebt();
    if (!debt) return false;

    var ids = giftVariantIds(debt.code);
    var units = giftUnitsIn(cart, ids);
    if (!units) {
      deleteCookie(DEBT_COOKIE);
      return false;
    }

    note('unfinished business: ' + debt.owed + ' gift unit(s) from ' + debt.code + ' still in the cart');

    var changed = false;
    var rounds = Math.min(debt.owed, units);

    for (var i = 0; i < rounds; i++) {
      if (!(await removeOneGiftUnit(ids))) break;
      payOneUnit(debt.code);
      changed = true;
    }

    return changed;
  }

  /* ------------------------------------------------------------ rule 1: the window */

  async function dropExpired(cart, token, origins) {
    var expired = managedCodes(cart, origins).filter(function (code) { return !isValid(code); });
    if (!expired.length) return false;

    note('rule 1: window closed for ' + expired.join(', ') + ' -- removing');

    var keep = cartCodes(cart).filter(function (code) {
      return !expired.some(function (gone) { return same(gone, code); });
    });

    await setCodes(keep);
    forget(expired, token, origins);
    await collectGiftUnits(expired);

    return true;
  }

  /* -------------------------------------------------- rule 2: newest click, on conflict */

  function entryFor(cart, code) {
    return (cart.discount_codes || []).filter(function (entry) { return same(entry.code, code); })[0];
  }

  // What the last arbitration was decided on: the code, the click serial, the codes on the
  // cart, and the cart itself. All four matter -- see the note at the top of the file.
  function fingerprint(cart, code, serial) {
    return [
      key(code),
      serial,
      cartCodes(cart).map(key).sort().join('+'),
      cart.item_count,
      cart.original_total_price,
      cart.items.map(function (item) { return item.variant_id + 'x' + item.quantity; }).sort().join(',')
    ].join('|');
  }

  async function resolveClickConflict(cart, token, origins, serial) {
    var clicked = clickedCode();
    if (!clicked) {
      note('rule 2: no click cookie -- no code was clicked in this session');
      return false;
    }

    var entry = entryFor(cart, clicked);
    if (!entry) {
      note('rule 2: ' + clicked + ' was clicked but is NOT on the cart -- the click never applied it');
      return false;
    }
    if (entry.applicable) {
      note('rule 2: ' + clicked + ' applies -- no conflict, nothing touched');
      return false;
    }

    var print = fingerprint(cart, clicked, serial);
    if (readCookie(ARBITRATED_COOKIE) === print) {
      note('rule 2: already arbitrated this exact cart for ' + clicked + ' -- skipping');
      return false;
    }

    var others = managedCodes(cart, origins).filter(function (code) {
      return !same(code, clicked);
    });
    if (!others.length) {
      note('rule 2: ' + clicked + ' is blocked, but no other managed code to remove');
      return false;
    }

    writeSessionCookie(ARBITRATED_COOKIE, print);
    note('rule 2: ' + clicked + ' is blocked -- trying without ' + others.join(', '));

    var before = cartCodes(cart);
    var keep = before.filter(function (code) {
      return !others.some(function (gone) { return same(gone, code); });
    });

    await setCodes(keep);
    var after = await getCart();
    var now = entryFor(after, clicked);

    if (!now || !now.applicable) {
      // The older codes were not the problem -- the clicked one cannot apply to this cart
      // at all. Put them back exactly as they were and change nothing.
      await setCodes(before);
      note('rule 2: still blocked without them -- something else blocks it, codes restored');
      return false;
    }

    forget(others, token, origins);
    await collectGiftUnits(others);

    note('rule 2: ' + clicked + ' now applies -- newest click won');
    return true;
  }

  /* --------------------------------------------------- rule 3: a gift is free or gone */

  async function dropUnfreeGift() {
    var config = window.WVFreeGift;
    if (!config || !config.enabled || !config.threshold || !config.giftCode) return false;

    var giftIds = giftVariantIds(config.giftCode);
    var excluded = (config.excludedVariantIds || []).map(Number);
    var threshold = Number(config.threshold);
    var cart = await getCart();

    var hasCode = (cart.discount_codes || []).some(function (entry) {
      return same(entry.code, config.giftCode);
    });
    if (!hasCode) return false; // no code, no promise of a gift -- her cart is her own

    var giftLines = giftLinesIn(cart, giftIds);
    if (!giftLines.length) return false;

    var honoured = giftLines.some(function (item) {
      return (item.line_level_discount_allocations || []).some(function (allocation) {
        var application = allocation.discount_application || {};
        return same(application.title, config.giftCode) && allocation.amount > 0;
      });
    });
    if (honoured) {
      note('rule 3: the gift is being discounted -- leaving it');
      return false;
    }

    var spend = cart.items.reduce(function (total, item) {
      var counts =
        giftIds.indexOf(item.variant_id) === -1 && excluded.indexOf(item.variant_id) === -1;
      return counts ? total + item.original_line_price : total;
    }, 0);
    if (spend < threshold) {
      note('rule 3: below the threshold -- the gift stays at its own price');
      return false;
    }

    note('rule 3: gift in the cart but not discounted -- removing a unit');
    return removeOneGiftUnit(giftIds);
  }

  /* ------------------------------------------------------------------------ driver */

  var running = false;
  var queued = false;
  var timer = null;

  // On the cart page, during a page load, the whole page is about these totals, so it comes
  // back from the server. Everywhere else -- and for anything triggered by her own cart
  // change, where a reload would throw away what she was doing -- the drawer and bag
  // counter are repainted in place.
  function showChanges(changed, allowReload) {
    if (!changed) return false;

    if (allowReload && /^\/cart(\/|$)/.test(window.location.pathname)) {
      note('reloading the cart page to show the new totals');
      window.location.reload();
      return true;
    }

    repaintCart();
    return false;
  }

  function whenSettled() {
    // window.WVFreeGift is defined near the end of the page, and free-gift.js has its own
    // pass to finish; judging the cart before that is judging a cart mid-settle.
    return new Promise(function (resolve) {
      var wait = function () { setTimeout(resolve, 1200); };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', wait);
      } else {
        wait();
      }
    });
  }

  async function pass(reason, allowReload) {
    if (running) { queued = true; return; }
    running = true;

    steps = [];
    note('start (' + reason + '): ' + window.location.pathname + ' | click cookie: ' + (clickedCode() || 'none'));

    try {
      openWindowFor(clickedCode());
      var serial = clickSerial();

      var cart = await getCart();
      var token = cart.token;
      var origins = readOrigins(token);

      note('cart codes: ' + ((cart.discount_codes || []).map(function (entry) {
        return entry.code + (entry.applicable ? '(applies)' : '(blocked)');
      }).join(', ') || 'none'));

      var dirty = false;
      pendingOrigins.forEach(function (code) {
        if (!Object.prototype.hasOwnProperty.call(origins, code)) {
          origins[code] = Date.now();
          dirty = true;
        }
      });
      if (dirty) writeOrigins(token, origins);

      // Anything we failed to finish last time comes first.
      var changed = await payGiftDebt(cart);
      if (changed) cart = await getCart();

      changed = (await dropExpired(cart, token, origins)) || changed;
      if (changed) cart = await getCart();

      changed = (await resolveClickConflict(cart, token, origins, serial)) || changed;
      if (showChanges(changed, allowReload)) return;

      if (reason === 'page load') await whenSettled();
      showChanges(await dropUnfreeGift(), allowReload);
    } catch (error) {
      // Never let discount housekeeping break the page or the cart.
      note('stopped on an error: ' + (error && error.message ? error.message : error));
    } finally {
      running = false;
      if (queued) {
        queued = false;
        schedule('queued change');
      }
    }
  }

  function schedule(reason) {
    clearTimeout(timer);
    timer = setTimeout(function () { pass(reason, false); }, 250);
  }

  pass('page load', true);

  // Her own cart changes re-open the question: adding the $19.90 piece after clicking the
  // 50% link loads no page at all.
  var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var originalFetch = window.fetch;

  window.fetch = function (resource, options) {
    var url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
    var headers = (options && options.headers) || {};
    var internal = headers[INTERNAL_HEADER] || (headers.get && headers.get(INTERNAL_HEADER));
    var result = originalFetch.apply(this, arguments);

    if (!internal && CART_WRITE.test(url)) {
      result.then(function (response) {
        if (response && response.ok) schedule('cart change');
      }).catch(function () {});
    }

    return result;
  };

  // Some of the theme's cart controls still use XHR rather than fetch.
  if (window.XMLHttpRequest) {
    var originalOpen = window.XMLHttpRequest.prototype.open;
    window.XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (CART_WRITE.test(String(url))) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) schedule('cart change');
          });
        }
      } catch (e) {}
      return originalOpen.apply(this, arguments);
    };
  }

  // Back/forward cache: the cart may have moved on, and the window may have closed while
  // the page sat in the cache.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) pass('back/forward cache', true);
  });
})()
